// Simple helpers for JSON-encoded browser cookies with optional TTL.

/**
 * @typedef {(string|number|boolean|Object|null)} CookieValue
 */

/**
 * @typedef {Object} CookieOptions
 * @property {number} [expiresInMs] Optional TTL in milliseconds.
 * @property {string} [path] Cookie path, defaults to '/'.
 * @property {string} [domain] Explicit cookie domain, if needed.
 * @property {boolean} [secure] Force the Secure flag on or off. Defaults to true on HTTPS origins and false otherwise.
 * @property {('Strict'|'Lax'|'None')} [sameSite] SameSite policy, defaults to 'Lax'.
 */

const DEFAULT_SAMESITE = 'Lax';

/**
 * Sets a cookie with a JSON-serialized value and optional expiry/attributes. Secure defaults to true on HTTPS origins.
 *
 * @param {string} key
 * @param {CookieValue} value
 * @param {CookieOptions} [options]
 */
export function setCookie(key, value, options = {}) {
  try {
    const isSecureContext =
      typeof window !== 'undefined' &&
      window.location &&
      window.location.protocol === 'https:';

    const {
      expiresInMs,
      path = '/',
      domain,
      secure = isSecureContext,
      sameSite = DEFAULT_SAMESITE,
    } = options;

    const serializedValue = encodeURIComponent(JSON.stringify(value));
    let cookieString = `${encodeURIComponent(key)}=${serializedValue}`;

    if (expiresInMs) {
      const expiryDate = new Date(Date.now() + expiresInMs);
      cookieString += `; expires=${expiryDate.toUTCString()}`;
    }

    if (path) {
      cookieString += `; path=${path}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    if (sameSite) {
      cookieString += `; SameSite=${sameSite}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error setting cookie "${key}":`, error);
  }
}

/**
 * Reads and parses a JSON-serialized cookie value.
 *
 * @param {string} key
 * @returns {CookieValue}
 */
export function getCookie(key) {
  try {
    if (!document.cookie) return null;

    const encodedKey = `${encodeURIComponent(key)}=`;
    const cookies = document.cookie.split(';');
    let rawValue = null;

    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(encodedKey)) {
        rawValue = cookie.substring(encodedKey.length);
        break;
      }
    }

    if (rawValue === null) {
      return null;
    }

    const decoded = decodeURIComponent(rawValue);
    return JSON.parse(decoded);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error getting cookie "${key}":`, error);
    return null;
  }
}

/**
 * Clears a cookie by setting its expiration to the past.
 *
 * @param {string} key
 * @param {Object} [options]
 * @param {string} [options.path] Cookie path, defaults to '/'.
 * @param {string} [options.domain] Explicit cookie domain, if needed.
 */
export function clearCookie(key, options = {}) {
  try {
    const { path = '/', domain } = options;

    let cookieString = `${encodeURIComponent(
      key,
    )}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    if (path) {
      cookieString += `; path=${path}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error clearing cookie "${key}":`, error);
  }
}
